// import { catalogoApi } from '../services/api';

import { toast } from 'react-toastify';
import { PatientProps } from '../contexts/PatientContext';
import { makePatientRuleByPatientProps, PatientValidateRule } from '../models/Patient';
import { PaymentValidationRule } from '../models/PaymentRule';
import { ProductItemOriginal } from '../models/Product';
import { Payment } from '../models/Schedule';
import { BusnessRule } from '../services/api';
import { ResponseAPI } from './responseApi';
import { ServiceItemProps } from '../models/Service';
import { RuleValidationCheckup, RuleValidationResponse, RuleValidationResponseData } from '../models/RuleValidator';
import { AxiosError } from 'axios';
import { InternalStatusCode } from '../utils/enums/validate-rules.enum';
import { isAxiosError } from '../utils/errors';
import { LateAppointmentError } from '../errors/LateAppointmentError';
import { UnavailableReturnsError } from '../errors/UnavailableReturnsError';
import { FirstAppointmentUnavailableError } from '../errors/FirstAppointmentUnavailableError';
import { SystemUnavailableError } from '../errors';
import { FirstAppointmentUnderTimeLimit } from '../errors/FirstAppointmentUnderTimeLimit';

type RuleValidationProps = {
  product: ProductItemOriginal;
  patient: PatientValidateRule;
  payment: PaymentValidationRule;
};

type CheckupValidationProps = {
  service: ServiceItemProps;
  patient: PatientValidateRule;
  payment: PaymentValidationRule;
};

type RuleValidationSpecialistProps = {
  patient: PatientProps;
  specialist: {
    specialist_id: string;
    office_id: string;
  };
  payment: Payment;
};

type ButtonTemplate = {
  text?: string;
  action?: string;
  value?: string;
};

export type ModalRuleValidation = {
  title: string;
  body: string;
  icon?: string;
  primary_button?: ButtonTemplate;
  second_button?: ButtonTemplate;
};

export type AdditionalInformations = {
  type: string;
  trigger: {
    when: string;
    condition: string;
  };
  content: ModalRuleValidation;
};

export const fetchRuleValidation = async (
  productValidation: RuleValidationProps
): Promise<ResponseAPI<RuleValidationResponse<RuleValidationResponseData>>> => {
  const result: ResponseAPI<RuleValidationResponse<RuleValidationResponseData>> = {
    error: false,
    errorStatus: null,
    message: '',
    data: {} as RuleValidationResponse<RuleValidationResponseData>
  };

  try {
    const response = await BusnessRule.post('rule-validator/validate-aps', productValidation);

    if (response.data.statusCode === InternalStatusCode.MULTIPLE_APPOINTMENTS_EXCEPTION) {
      result.error = true;
      result.errorStatus = InternalStatusCode.MULTIPLE_APPOINTMENTS_EXCEPTION;
    }
    result.data = response.data;

    return result;
  } catch (error) {
    result.error = true;
    console.error(error);
    return result;
  }
};

export const fetchRuleValidationSpecialist = async (
  productValidation: RuleValidationSpecialistProps
): Promise<ResponseAPI<RuleValidationResponse<RuleValidationResponseData>>> => {
  const result: ResponseAPI<RuleValidationResponse<RuleValidationResponseData>> = {
    error: false,
    errorStatus: null,
    message: '',
    data: {} as RuleValidationResponse<RuleValidationResponseData>
  };

  try {
    const patientRule = makePatientRuleByPatientProps(productValidation.patient);

    const response = await BusnessRule.post('rule-validator/validate-ass', {
      ...productValidation,
      patient: patientRule
    });
    result.data = response.data;
    return result;
  } catch (error) {
    result.error = true;
    console.error(error);
    return result;
  }
};

export const fetchValidateCheckup = async ({ patient, payment, service }: CheckupValidationProps) => {
  try {
    const response = await BusnessRule.post('rule-validator/validate-checkup', {
      service,
      patient,
      payment
    });

    return response.data.data as RuleValidationCheckup;
  } catch (e) {
    const error = e as AxiosError;
    if (isAxiosError(error)) {
      const message = error.response?.data.message;
      const statusCode = error.response?.data.statusCode;

      if (statusCode === InternalStatusCode.INTERVAL_AFTER_FIRST_APPOINTMENT_EXCEPTION)
        throw new FirstAppointmentUnderTimeLimit(message);
      if (message === 'First appointment over 90 days') throw new LateAppointmentError(message);
      if (message === 'First appointment are already scheduled.') throw new UnavailableReturnsError(message);
      if (message === 'First appointment unavailable') throw new FirstAppointmentUnavailableError(message);
    }

    throw new SystemUnavailableError(error.message);
  }
};

export const fetchValidateExecutiveCheckup = async ({ patient, payment, service }: CheckupValidationProps) => {
  try {
    const response = await BusnessRule.post('rule-validator/validate-checkup-executive', {
      service,
      patient,
      payment
    });

    return response.data.data;
  } catch (e) {
    const error = e as AxiosError;
    if (isAxiosError(error)) {
      const message = error.response?.data.message;
      const statusCode = error.response?.data.statusCode;

      if (statusCode === InternalStatusCode.INTERVAL_AFTER_FIRST_APPOINTMENT_EXCEPTION)
        throw new FirstAppointmentUnderTimeLimit(message);
      if (message === 'First appointment over 90 days') throw new LateAppointmentError(message);
      if (message === 'First appointment are already scheduled.') throw new UnavailableReturnsError(message);
      if (message === 'First appointment unavailable') throw new FirstAppointmentUnavailableError(message);
    }

    throw new SystemUnavailableError(error.message);
  }
};

export const fetchValidateFirstAppointment = async (params: {
  serviceId: string;
  document: string;
  documentType: string;
}) => {
  const result: ResponseAPI<{ isFirstAppointment: boolean }> = {
    error: false,
    errorStatus: null,
    message: '',
    data: { isFirstAppointment: false }
  };
  try {
    const response = await BusnessRule.get('/rule-validator/validate-first-appointment', { params });
    result.data = response.data;

    if (response.data.statusCode === InternalStatusCode.OK) result.data = response?.data?.data;
    else result.error = true;

    return result;
  } catch (e) {
    const error = e as AxiosError;
    result.error = true;
    result.errorStatus = error.response?.data.statusCode;
    console.error(error);
    toast.error('Erro ao validar serviço');
    return result;
  }
};
